import { useEffect, useRef } from 'react'
import { useStudioStore } from '@/store/studio-store'
import { isReadableReport, selectTopViolations } from './select-top-violations'

/**
 * Phase D bridge — correlates the end of a dispatch (`dispatchedOutput.kind ===
 * 'rendered'`) with the next `validatorLastRunAt` advance from Phase C, emitting
 * exactly one summary + ≤3 issue messages + optional overflow link to chat.
 *
 * Single-shot per dispatch via `expectingRunAfter` ref — once an emit completes,
 * the ref clears and a new rendered output must arrive before the next emit.
 *
 * Mounted by ChatRail.tsx in build mode only (T10). Skip-on-falsy-report and
 * stale-validation skips ensure no false emits.
 */
export function useDispatchValidationBridge(): void {
  const dispatchedOutput = useStudioStore((s) => s.dispatchedOutput)
  const validationReport = useStudioStore((s) => s.validationReport)
  const validatorLastRunAt = useStudioStore((s) => s.validatorLastRunAt)
  const appendChatMessage = useStudioStore((s) => s.appendChatMessage)

  const expectingRunAfter = useRef<number | null>(null)
  const validatorLastRunAtRef = useRef(validatorLastRunAt)
  validatorLastRunAtRef.current = validatorLastRunAt

  useEffect(() => {
    if (dispatchedOutput && dispatchedOutput.kind === 'rendered') {
      // Snapshot the current validatorLastRunAt so we wait for a strictly
      // newer run. Using -1 when null so any real timestamp satisfies.
      expectingRunAfter.current = validatorLastRunAtRef.current ?? -1
    }
  }, [dispatchedOutput])

  useEffect(() => {
    if (
      expectingRunAfter.current === null ||
      validatorLastRunAt === null ||
      validatorLastRunAt <= expectingRunAfter.current ||
      !validationReport
    ) {
      return
    }

    expectingRunAfter.current = null

    const hasSummaryForRun = useStudioStore.getState().chatMessages.some((message) => (
      message.kind === 'validation-summary' &&
      message.id.startsWith('generation-validation-') &&
      message.timestamp >= validatorLastRunAt
    ))
    if (hasSummaryForRun) return

    // 'idle' means the validator never produced a verdict for this run
    // (e.g. it threw before reporting) — announcing it as 'pass' would be
    // a fail-open. Emit nothing rather than a green badge.
    if (validationReport.status === 'idle') return

    // Task 7 (slice 1) — a report that DID run but whose shape
    // `selectViolations` cannot walk (missing/malformed blocks or phases) is
    // a DIFFERENT failure from 'idle': something happened, but this bridge
    // cannot vouch for what. Going silent here — the way this code used to
    // just throw and let the render crash swallow the turn — is the SAME
    // fail-open the 'idle' guard above exists to refuse, dressed differently:
    // an empty chat dock reads to the user as "nothing to report", not as
    // "validation broke". Disclose it instead of dropping it or crashing on
    // it (selectTopViolations below walks the same unguarded shape that used
    // to throw and take the whole Studio shell down with it — see
    // `select-top-violations.ts`'s own comment).
    if (!isReadableReport(validationReport)) {
      appendChatMessage({
        kind: 'validation-summary',
        id: crypto.randomUUID(),
        timestamp: Date.now(),
        status: 'unreadable',
        pass: 0,
        violations: 0,
        warnings: 0,
        blockCount: 0,
        durationMs: validationReport.durationMs ?? 0,
      })
      return
    }

    const status = validationReport.status

    appendChatMessage({
      kind: 'validation-summary',
      id: crypto.randomUUID(),
      timestamp: Date.now(),
      status,
      pass: validationReport.pass,
      violations: validationReport.violations,
      warnings: validationReport.warnings,
      blockCount: validationReport.blocks.length,
      durationMs: validationReport.durationMs,
    })

    const top = selectTopViolations(validationReport, 3)
    const overflowCount = validationReport.violations - top.length

    for (const v of top) {
      appendChatMessage({
        kind: 'validation-issue',
        id: crypto.randomUUID(),
        timestamp: Date.now(),
        isOverflow: false,
        blockId: v.blockId,
        blockName: v.blockName,
        elementId: v.elementId,
        elementType: v.elementType,
        ruleId: v.ruleId,
        message: v.message,
        severity: 'violation',
      })
    }

    if (overflowCount > 0) {
      appendChatMessage({
        kind: 'validation-issue',
        id: crypto.randomUUID(),
        timestamp: Date.now(),
        isOverflow: true,
        remainingCount: overflowCount,
      })
    }
  }, [validationReport, validatorLastRunAt, appendChatMessage])
}
